import Image from "next/image";
import { cn } from "../../lib/utils";

type LogoAvatarSize = 44 | 48 | 56;

interface LogoAvatarProps {
  alt: string;
  className?: string;
  name: string;
  size: LogoAvatarSize;
  src: string | null | undefined;
}

const SIZE_CLASSES: Record<
  LogoAvatarSize,
  { fallbackText: string; sizes: string; wrapper: string }
> = {
  44: {
    fallbackText: "font-semibold text-sm",
    sizes: "44px",
    wrapper: "h-11 w-11 rounded-xl",
  },
  48: {
    fallbackText: "font-bold text-xs",
    sizes: "48px",
    wrapper: "h-12 w-12 rounded-md",
  },
  56: {
    fallbackText: "font-bold",
    sizes: "56px",
    wrapper: "h-14 w-14 rounded-lg",
  },
};

// Renders a brand logo inside a square tile using `object-cover` on a muted
// tile background. Source logos vary widely — square photos, transparent
// PNGs, banners with built-in padding — and no CSS technique can fully
// normalise across that variance. The trade-off accepted here: `cover` fills
// the tile and crops away built-in padding, at the cost of edge-cropping
// wide wordmarks. Long-term fix is upload-time normalisation (trim padding,
// square-pad with dominant colour) — see follow-up issue.
//
// Distinct from the Radix-based `Avatar` (which is round, for people).
function LogoAvatar({ alt, className, name, size, src }: LogoAvatarProps) {
  const { fallbackText, sizes, wrapper } = SIZE_CLASSES[size];
  const initial = name.charAt(0).toUpperCase() || "?";

  return (
    <div
      className={cn(
        "relative shrink-0 overflow-hidden bg-muted",
        wrapper,
        className
      )}
    >
      {src ? (
        <Image
          alt={alt}
          className="object-cover"
          fill
          sizes={sizes}
          src={src}
        />
      ) : (
        <div
          className={cn(
            "flex h-full w-full items-center justify-center text-muted-foreground",
            fallbackText
          )}
        >
          {initial}
        </div>
      )}
    </div>
  );
}

export { LogoAvatar, type LogoAvatarProps, type LogoAvatarSize };
